/**
 * TourOverlay — the full-screen demo-tour cockpit.  Finds the active
 * step's target element by `data-testid`, draws a glowing spotlight
 * ring around it, scrolls it into view, and parks a caption bubble +
 * animated arrow + progress dots + playback controls below.
 *
 * Mounted inside the GuidedTourProvider.  Does nothing when the tour
 * is idle.
 */
import React, { useEffect, useRef, useState } from "react";
import { useGuidedTour } from "@/lib/guidedTour";
import { ChevronLeft, ChevronRight, Pause, Play, X, Volume2, Pencil, CheckCircle2 } from "lucide-react";
import TourLineEditor from "./TourLineEditor";
import BookCallForm from "./BookCallForm";
import { useDemoBrand } from "@/lib/demoBrand";

const DEFAULT_TOUR_COLORS = { ring: "6, 182, 212", ring2: "16, 185, 129", arrow: "#06b6d4" };

const SPOTLIGHT_PAD = 14;
const STRIP_CLEARANCE = 96; // keep bubbles below the sticky tour bar
const SPOTLIGHT_PAD_LG = 22;

export default function TourOverlay({ isAdmin = false, onLineSaved, autoScroll = true, bottomBarBelow = 0, hideTextBelow = 0 }) {
  const { isActive, isPaused, current, step, pending, script, next, prev, stop, togglePause, isAuto, pauseForEdit, replay } = useGuidedTour();
  const { tourColors } = useDemoBrand();
  const tc = tourColors || DEFAULT_TOUR_COLORS;
  const [rect, setRect] = useState(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => { setEditing(false); }, [step, isActive]);
  const captionRef = useRef(null);

  // Measure the active target element and keep the bbox in sync on
  // scroll + resize.  If the target isn't on screen yet we scroll it
  // into view.
  useEffect(() => {
    if (!isActive || !current || pending) { setRect(null); return; }

    let raf = 0;
    const measure = () => {
      const el = document.querySelector(`[data-testid="${current.target}"]`);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top, left: r.left, width: r.width, height: r.height,
        bottom: r.bottom, right: r.right,
      });
    };

    const scrollTarget = () => {
      const el = document.querySelector(`[data-testid="${current.target}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const viewportMid = window.innerHeight / 2;
      // Scroll if target not comfortably in view.
      if (r.top < 120 || r.bottom > window.innerHeight - 120) {
        window.scrollTo({
          top: window.scrollY + r.top - viewportMid + r.height / 2,
          behavior: "smooth",
        });
      }
    };

    if (autoScroll) scrollTarget();
    const t = setTimeout(() => {
      raf = requestAnimationFrame(measure);
    }, 420); // allow scroll to settle

    const onScroll = () => { raf = requestAnimationFrame(measure); };
    const onResize = () => { raf = requestAnimationFrame(measure); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    const targetEl = document.querySelector(`[data-testid="${current.target}"]`);
    const ro = targetEl && typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null;
    if (ro) ro.observe(targetEl);

    return () => {
      clearTimeout(t);
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [isActive, current, step, pending, autoScroll]);

  if (!isActive || !current || pending) return null;

  const isSummary = current.type === "summary";
  const isLg = rect ? rect.width > 560 : false;
  const pad = isLg ? SPOTLIGHT_PAD_LG : SPOTLIGHT_PAD;

  // Caption bubble sits below the target if there's room, else above.
  // 320px bubble, 14px gap.
  const BUBBLE_W  = 420;
  const BUBBLE_H  = captionRef.current?.offsetHeight || 220;
  const vh = typeof window !== "undefined" ? window.innerHeight : 900;
  const vw = typeof window !== "undefined" ? window.innerWidth  : 1600;
  let bubbleTop, bubbleLeft, arrowDir;
  if (rect) {
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top - (document.querySelector('[data-testid="veroogle-tour-strip"]')?.getBoundingClientRect().bottom || STRIP_CLEARANCE);
    const spaceRight = vw - rect.right;
    if (spaceBelow > BUBBLE_H + 60) {
      bubbleTop  = rect.bottom + pad + 16;
      arrowDir   = "up";
    } else if (spaceAbove < BUBBLE_H + 40 && spaceRight > BUBBLE_W + pad + 40) {
      bubbleTop  = rect.top + rect.height / 2 - BUBBLE_H / 2;
      arrowDir   = "side";
    } else {
      bubbleTop  = rect.top - pad - 16 - BUBBLE_H;
      arrowDir   = "down";
    }
    // Clamp vertically so the controls are always reachable, even
    // when the spotlight target spans the full viewport.
    const stripBottom = document.querySelector('[data-testid="veroogle-tour-strip"]')?.getBoundingClientRect().bottom || 0;
    bubbleTop  = Math.max(STRIP_CLEARANCE, stripBottom + 8, Math.min(vh - BUBBLE_H - 16, bubbleTop));
    bubbleLeft = arrowDir === "side"
      ? rect.right + pad + 24
      : Math.max(16, Math.min(vw - BUBBLE_W - 16, rect.left + rect.width / 2 - BUBBLE_W / 2));
    // Optional dock: a beat can reserve a visible slot inside its target for the caption.
    const dock = current.dock && document.querySelector(`[data-testid="${current.dock}"]`)?.getBoundingClientRect();
    if (dock && dock.width >= BUBBLE_W - 20) {
      arrowDir = "side";
      bubbleLeft = dock.left + (dock.width - BUBBLE_W) / 2;
      bubbleTop = Math.max(stripBottom + 8, Math.min(vh - BUBBLE_H - 16, dock.top + (dock.height - BUBBLE_H) / 2));
    }
  }
  // Narrower than desktop: dock the caption as a full-width bar along the bottom so it never covers the visuals.
  // Below `hideTextBelow` the narration text is dropped entirely — only a slim control bar remains.
  const noText = !isSummary && vw < hideTextBelow;
  const bottomBar = !isSummary && (noText || vw < bottomBarBelow);
  if (bottomBar) arrowDir = "side";

  return (
    <div className="fixed inset-0 z-[90] animate-in fade-in duration-300" data-testid="veroogle-tour-overlay">
      {/* Capture clicks outside the bubble so the client can't fiddle
          with the page mid-narration.  Placed first so later siblings
          (caption + arrow) paint on top and receive their own clicks. */}
      <div
        className="absolute inset-0"
        style={{ pointerEvents: "auto" }}
        onClick={(e) => {
          if (captionRef.current && captionRef.current.contains(e.target)) return;
          e.stopPropagation();
        }}
      />

      {/* Dim backdrop with a cutout for the target.  box-shadow spread
          fills everything OUTSIDE the spotlight. */}
      {/* Phones/tablets in text-free mode: no dim, ring or arrow — huge animated box-shadows black out mobile GPUs. */}
      {noText ? null : rect ? (
        <div
          className="absolute pointer-events-none transition-all duration-300"
          style={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 18,
            boxShadow: "0 0 0 9999px rgba(5, 10, 25, 0.78)",
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-[rgba(5,10,25,0.78)]" />
      )}

      {/* Glowing ring around the spotlight */}
      {rect && !noText && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 18,
            boxShadow:
              `0 0 0 2px rgba(${tc.ring}, 0.9), 0 0 32px 6px rgba(${tc.ring}, 0.35), inset 0 0 0 1px rgba(255,255,255,0.08)`,
            animation: "veroogleTourPulse 2.4s ease-in-out infinite",
          }}
        />
      )}

      {/* Pulse keyframes */}
      <style>{`
        @keyframes veroogleTourPulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(${tc.ring}, 0.9), 0 0 32px 6px rgba(${tc.ring}, 0.35), inset 0 0 0 1px rgba(255,255,255,0.08); }
          50%      { box-shadow: 0 0 0 2px rgba(${tc.ring2}, 1.0), 0 0 44px 10px rgba(${tc.ring2}, 0.42), inset 0 0 0 1px rgba(255,255,255,0.12); }
        }
        @keyframes veroogleArrowBob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
      `}</style>

      {/* Caption bubble + arrow */}
      {(rect || isSummary || noText) && (
        <>
          {/* Arrow */}
          {rect && !isSummary && !noText && arrowDir !== "side" && <div
            className="absolute pointer-events-none"
            style={{
              top: arrowDir === "up"
                ? rect.bottom + pad + 2
                : rect.top - pad - 14,
              left: Math.max(16, Math.min(vw - 24, rect.left + rect.width / 2 - 12)),
              animation: "veroogleArrowBob 1.4s ease-in-out infinite",
            }}
          >
            <svg width="24" height="14" viewBox="0 0 24 14" fill="none">
              {arrowDir === "up" ? (
                <path d="M12 0 L24 14 L0 14 Z" fill={tc.arrow} />
              ) : (
                <path d="M12 14 L24 0 L0 0 Z" fill={tc.arrow} />
              )}
            </svg>
          </div>}

          {/* Caption bubble */}
          <div
            ref={captionRef}
            data-testid={isSummary ? "veroogle-tour-summary-card" : "veroogle-tour-caption"}
            className={isSummary
              ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-32px)] overflow-y-auto rounded-3xl border border-emerald-400/40 bg-[rgba(12,19,34,0.97)] backdrop-blur-xl shadow-[0_0_60px_rgba(16,185,129,0.18),0_20px_60px_rgba(0,0,0,0.55)] p-7 text-slate-100 z-[2] animate-in fade-in zoom-in-95 duration-300"
              : "absolute w-[420px] max-w-[calc(100vw-32px)] rounded-2xl border border-brand-cyan/40 bg-[rgba(12,19,34,0.96)] backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.55)] p-5 text-slate-100 z-[2]"}
            data-placement={noText ? "controls-only" : bottomBar ? "bottom-bar" : "float"}
            style={isSummary ? { pointerEvents: "auto" } : bottomBar
              ? { left: 12, right: 12, bottom: 12, width: "auto", maxWidth: "none", maxHeight: "40vh", overflowY: "auto", pointerEvents: "auto", ...(noText && { padding: "10px 14px" }) }
              : { top: bubbleTop, left: bubbleLeft, pointerEvents: "auto" }}
          >
            {!noText && <>
            <div className="flex items-center gap-2 text-brand-cyan font-mono text-[10px] tracking-[0.18em] uppercase mb-2">
              <Volume2 className="w-3.5 h-3.5" /> {current.kicker}
              {isAdmin && !editing && (
                <button
                  onClick={() => { pauseForEdit(); setEditing(true); }}
                  data-testid="veroogle-tour-edit-line"
                  className="ml-auto p-1 rounded-md text-slate-400 hover:text-brand-cyan hover:bg-white/5 transition-colors"
                  aria-label="Edit narration"
                  title="Edit spoken wording"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className={`font-semibold text-white leading-tight ${isSummary ? "text-2xl mb-4" : "text-lg mb-2"}`}>
              {current.title}
            </div>
            {editing ? (
              <TourLineEditor
                step={current.step}
                text={current.caption}
                edited={current.edited}
                onCancel={() => setEditing(false)}
                onSaved={(line) => { onLineSaved?.(line); setEditing(false); replay(); }}
              />
            ) : isSummary ? (
              <ul className="space-y-2.5" data-testid="veroogle-tour-summary-bullets">
                {current.bullets.map((b, i) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-slate-200 text-[15px] leading-snug animate-in fade-in slide-in-from-left-2 fill-mode-both"
                    style={{ animationDelay: `${150 + i * 120}ms`, animationDuration: "400ms" }}
                  >
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" /> {b}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-300 text-sm leading-relaxed" data-testid="veroogle-tour-caption-text">{current.caption}</p>
            )}
            {current.cta === "book-call" && !editing && <BookCallForm key={current.id} onOpen={pauseForEdit} />}
            </>}

            {/* Controls */}
            <div data-testid="veroogle-tour-controls" className={`${noText ? "" : "mt-4 pt-4 border-t border-white/10 "}flex items-center justify-between gap-2`}>
              <div className="flex items-center gap-1.5" data-testid="veroogle-tour-dots">
                {script.map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full transition ${i === step ? "bg-brand-cyan w-5" : i < step ? "bg-slate-500" : "bg-slate-700"}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={prev}
                  disabled={step === 0}
                  data-testid="veroogle-tour-prev"
                  className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {isAuto && (
                  <button
                    onClick={togglePause}
                    data-testid="veroogle-tour-pause"
                    className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition"
                    aria-label={isPaused ? "Resume" : "Pause"}
                  >
                    {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                  </button>
                )}
                <button
                  onClick={next}
                  data-testid="veroogle-tour-next"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-mono text-[10px] font-semibold tracking-[0.14em] uppercase transition"
                >
                  Next <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={stop}
                  data-testid="veroogle-tour-exit"
                  className="p-1.5 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition ml-1"
                  aria-label="Exit tour"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
